import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { tableHeader2, columns } from "../NetPositions/FnOUtility";
import usePersistStoreData from "../../Components/Hooks/usePersistStoreData";
import exportToPDF from "../../Components/Common/exportToPDF";
import { showToast } from "../../Components/ToastNotification";
import {
  formatDateDay,
  getCurrentFiscalYearDates,
  getPreviousMonthWorkingDates,
  getWorkingDateRange,
} from "../../Components/Common/WorkingDateRange";

const Financial_Ledger_MTFonly_Pdf = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const { userData } = usePersistStoreData();

  const getPreviousMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1); // Get previous month
    return date.toLocaleString("en-US", { month: "long" }).toUpperCase(); // Convert to uppercase
  };

  const now = new Date();

  // Get the first day of the previous month (1st of last month)
  const start_date = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  // Get the last day of the previous month
  const end_date = new Date(now.getFullYear(), now.getMonth(), 0); // 0th day of current month

  // Format the date as "YYYY-MM-DD"
  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`; // Changed order here
  };

  const columns = useMemo(
    () => [
      {
        id: "settlement_date",
        Header: "Set. Date",
        accessorKey: "settlement_date",
      },
      { id: "exchange", Header: "Exchange", accessorKey: "exchange" },
      { id: "segment", Header: "Segment", accessorKey: "segment" },
      { id: "particulars", Header: "Particulars", accessorKey: "particulars" },
      {
        id: "debit",
        Header: "Debit (₹)",
        accessorKey: "debit",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          // console.log("row", row);
          return (
            <>
              <span class="  mb-0">
                {row?.getValue()
                  ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                  : 0}{" "}
              </span>
            </>
          );
        },
      },
      {
        id: "credit",
        Header: "Credit (₹)",
        accessorKey: "credit",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          // console.log("row", row);
          return (
            <>
              <span class="  mb-0">
                {row?.getValue()
                  ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                  : 0}{" "}
              </span>
            </>
          );
        },
      },

      {
        id: "running_balance",
        Header: "Balance (₹)",
        accessorKey: "running_balance",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          // console.log("row", row);
          return (
            <>
              {row?.getValue() > 0 ? (
                <span class=" text-success mb-0">
                  {row?.getValue()
                    ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                    : 0}
                  {/* <i class="ms-1 ri-arrow-up-line align-middle"></i> */}
                </span>
              ) : row?.getValue() < 0 ? (
                <span class=" text-danger mb-0">
                  {row?.getValue()
                    ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                    : 0}{" "}
                  {/* <i class="ms-1 ri-arrow-down-line align-middle"></i> */}
                </span>
              ) : (
                <span class="  mb-0">
                  {row?.getValue()
                    ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                    : 0}{" "}
                </span>
              )}
            </>
          );
        },
      },
    ],
    []
  );

  const { fyStart } = getCurrentFiscalYearDates();
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
      const endDate = today;

  const handleExport = async () => {
     // Determine the previous month from today's date.
     const today = new Date();
     // JavaScript month indexes: 0=January, 1=February, 2=March, etc.
     const prevMonth = today.getMonth() === 0 ? 11 : today.getMonth() - 1;

     // If the previous month is March (index 2), show error and do not call the API.
     if (prevMonth === 2) {
       showToast("No report is available for previous month of current FY", "error");
       return;
     }
    try {
      setLoading(true);

      // const { startDate, endDate } = getPreviousMonthWorkingDates();

      // First fetch the data
      const response = await axios.get("financial-ledger", {
        params: {
          start_date: formatDate(fyStart),
          end_date: formatDate(endDate),
          type: "mtf-only",
        },
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "Content-Type": "application/json",
        },
      });

      const summary = response?.data?.summary;
      const data = response.data?.items;

      const tableHeaders = columns.map((col) => col.Header.replace(/₹/g, "Rs"));

      const tableBody = data.map((row) =>
        columns.map((col) =>
          row[col.accessorKey]
            ? typeof row[col.accessorKey] === "string"
              ? row[col.accessorKey]
              : row[col.accessorKey].toLocaleString("en-IN")
            : 0
        )
      );

      // Define which columns to sum
      const sumColumns = ["debit", "credit"];
      const apiTotals = {
        debit: summary?.total_debit,
        credit: summary?.total_credit,
      };

      // Calculate totals only for "debit" and "credit"
      const totals = columns.map((col, index) => {
        if (index === 0) return "TOTAL"; // First column will have "Total"

        return sumColumns.includes(col.accessorKey)
          ? apiTotals[col.accessorKey]?.toLocaleString("en-IN") || 0 // Use API total, default to 0 if missing
          : ""; // Leave other columns empty
      });

      // Add totals row to the table
      tableBody.push(totals);

      // Use the response data directly for the PDF export instead of relying on state
      exportToPDF(
        "Financial Ledger",
        userData,
        `Combined`,
        // `From: ${formatDate(start_date)} - To: ${formatDate(end_date)}`,
        `From: ${formatDateDay(fyStart)} - To: ${formatDateDay(endDate)}`,

        tableHeaders,
        tableBody
      );
    } catch (error) {
      console.error("Failed to export PDF due to API error", error);
      showToast("Something went wrong please try again", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="d-flex border-bottom mt-3"
        style={{ cursor: "pointer" }}
        onClick={handleExport}
      >
        <div className="d-flex align-items-center justify-content-between mb-2 w-100">
          <div className="d-flex flex-column">
            <h6 className="text-primary fw-semibold fs-14">
              {getPreviousMonth()} - Financial Ledger Report (MTF Only)
            </h6>
            <div className="d-flex">
              <p className="fs-14 fw-medium text-muted me-2 mb-1 m-0 text-decoration-underline">
                {loading ? "Downloading..." : "Download"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Financial_Ledger_MTFonly_Pdf;
